import { useEffect } from 'react';

/**
 * useSEO — drop-in SEO meta-tag setter for marketing pages.
 *
 * Lightweight alternative to react-helmet — sets document.title and
 * patches/inserts <meta> + Open Graph + Twitter Card tags directly into
 * <head>. Works without any extra deps.
 *
 * Why not react-helmet? Adding a dep just for the landing page is
 * overkill, and SSR isn't a concern here (this is a CRA SPA). For the
 * landing page's 8 meta tags, hand-rolling is simpler.
 *
 * Usage:
 *   useSEO({
 *     title: 'SearchJob.ai — Hire smarter, screen 10× faster',
 *     description: '...',
 *     ogImage: '/og-image.png',
 *     canonical: 'https://searchjob.ai/',
 *   });
 *
 * What it patches:
 *   - <title>
 *   - <meta name="description">
 *   - <meta name="keywords">
 *   - <link rel="canonical">
 *   - <meta property="og:title|description|image|url|type|site_name">
 *   - <meta name="twitter:card|title|description|image|site">
 *
 * IMPORTANT: For the OG image to actually show on LinkedIn/WhatsApp
 * previews, it must be 1200x630, PNG/JPG, under 1MB, hosted at an
 * absolute URL. Put the file in /public/og-image.png and reference it
 * via your domain (not a relative path).
 */
export default function useSEO({
  title,
  description,
  keywords,
  ogImage,
  canonical,
  type = 'website',
  twitterHandle = '@searchjob_ai',
} = {}) {
  useEffect(() => {
    // ─── <title> ─────────────────────────────────────────────────
    if (title) document.title = title;

    // ─── Helper to set/create a <meta> tag ───────────────────────
    const setMeta = (selector, attr, attrValue, content) => {
      if (!content) return;
      let el = document.head.querySelector(selector);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, attrValue);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    // Standard meta
    setMeta('meta[name="description"]', 'name', 'description', description);
    setMeta('meta[name="keywords"]', 'name', 'keywords', keywords);

    // Open Graph (Facebook, LinkedIn, WhatsApp, Slack)
    setMeta('meta[property="og:title"]', 'property', 'og:title', title);
    setMeta('meta[property="og:description"]', 'property', 'og:description', description);
    setMeta('meta[property="og:image"]', 'property', 'og:image', ogImage);
    setMeta('meta[property="og:url"]', 'property', 'og:url', canonical);
    setMeta('meta[property="og:type"]', 'property', 'og:type', type);
    setMeta('meta[property="og:site_name"]', 'property', 'og:site_name', 'SearchJob.ai');

    // Twitter Card
    setMeta('meta[name="twitter:card"]', 'name', 'twitter:card', 'summary_large_image');
    setMeta('meta[name="twitter:title"]', 'name', 'twitter:title', title);
    setMeta('meta[name="twitter:description"]', 'name', 'twitter:description', description);
    setMeta('meta[name="twitter:image"]', 'name', 'twitter:image', ogImage);
    setMeta('meta[name="twitter:site"]', 'name', 'twitter:site', twitterHandle);

    // ─── <link rel="canonical"> ──────────────────────────────────
    if (canonical) {
      let link = document.head.querySelector('link[rel="canonical"]');
      if (!link) {
        link = document.createElement('link');
        link.setAttribute('rel', 'canonical');
        document.head.appendChild(link);
      }
      link.setAttribute('href', canonical);
    }
  }, [title, description, keywords, ogImage, canonical, type, twitterHandle]);
}
